// La résidence principale : l'acheter avec un crédit immobilier, en être propriétaire, rembourser par
// anticipation, la revendre. Le crédit est un prêt comme les autres (state.loans), avec son assurance.
import type { TextKey } from '../content/fr';
import { MORTGAGE, PROPERTIES, type MortgageYears } from '../content/property';
import { loanMonthlyPayment, round2 } from '../engine/finance';
import { fmtEur, fmtPct } from '../engine/format';
import { t } from '../engine/i18n';
import type { ExpenseId, GameState, Loan, MortgageInsurance, PropertyKind } from '../engine/state';
import { ageOnDay } from '../engine/time';
import { addJournal, addLoan, addNotice, addWellbeing, payFromSavings, unlockPage } from './actions';
import { atTodayPrices, monthlyIncome, monthlyLoanPayments } from './economy';
import { HOUSING } from '../content/paths';
import { moveOut, moveOutCost } from './housing';
import { LIVRET_A, regulatedSavings } from './livret';
import { changeSubscription, findSubscription, subscribe } from './subscriptions';

/** Le prix d'un bien aujourd'hui : son prix de départ, au niveau de la courbe de l'immobilier, arrondi à 1 000 €. */
export function propertyPrice(state: GameState, kind: PropertyKind): number {
  return Math.round((PROPERTIES[kind].price * state.market.realEstate) / 100 / 1000) * 1000;
}

/** Le crédit de la résidence principale, tant qu'il court. */
export function mortgageLoan(state: GameState): Loan | undefined {
  return state.loans.find((loan) => loan.mortgage);
}

/** La valeur estimée de la résidence principale : son prix d'achat, au rythme de la courbe de l'immobilier. */
export function homeValue(state: GameState): number {
  const home = state.home;
  if (!home) return 0;
  return round2((home.price * state.market.realEstate) / home.indexAtPurchase);
}

/** Ce que coûte le logement chaque mois : le loyer, ou pour un propriétaire le crédit, la copropriété et la taxe foncière. */
export function housingCost(state: GameState): number {
  if (state.housing !== 'proprio') return state.expenses.logement?.amount ?? 500;
  const loan = mortgageLoan(state);
  const credit = loan ? loan.monthlyPayment + (loan.insuranceMonthly ?? 0) : 0;
  return round2(credit + (state.expenses.copropriete?.amount ?? 0) + (state.expenses.taxeFonciere?.amount ?? 0));
}

/** L'argent disponible pour un apport ou un remboursement : le compte courant et les livrets. */
export function moneyAvailable(state: GameState): number {
  return Math.floor(Math.max(0, state.cash) + regulatedSavings(state));
}

function fileFee(state: GameState): number {
  return atTodayPrices(state, MORTGAGE.fileFee);
}

/** L'apport minimum : de quoi payer les frais (notaire, garantie, dossier) quand on emprunte tout le prix. */
export function minimumApport(state: GameState, kind: PropertyKind): number {
  const price = propertyPrice(state, kind);
  return Math.ceil(price * (MORTGAGE.notaryRate + MORTGAGE.guaranteeRate) + fileFee(state));
}

export interface PurchasePlan {
  kind: PropertyKind;
  price: number;
  notary: number;
  guarantee: number;
  fileFee: number;
  /** L'apport réellement utilisé : au plus le prix et les frais d'un achat sans crédit. */
  apport: number;
  loan: number;
  years: MortgageYears;
  rate: number;
  /** La mensualité du crédit, hors assurance. */
  payment: number;
  insurance: MortgageInsurance;
  insuranceMonthly: number;
  /** La mensualité assurance comprise. */
  monthly: number;
  totalInterest: number;
  totalInsurance: number;
  /** La part du salaire que prendraient tous les crédits, assurance comprise. */
  debtRatio: number;
}

/** Ce que coûterait un achat : les frais, la somme empruntée, la mensualité et le taux d'endettement. */
export function purchasePlan(
  state: GameState,
  kind: PropertyKind,
  apport: number,
  years: MortgageYears,
  insurance: MortgageInsurance,
): PurchasePlan {
  const price = propertyPrice(state, kind);
  const notary = Math.round(price * MORTGAGE.notaryRate);
  const file = fileFee(state);
  const used = Math.max(0, Math.min(apport, price + notary + file));
  // La garantie se calcule sur la somme empruntée, qui l'inclut elle-même.
  const loan = Math.max(0, Math.round((price + notary + file - used) / (1 - MORTGAGE.guaranteeRate)));
  const guarantee = loan > 0 ? round2(loan - (price + notary + file - used)) : 0;
  const rate = MORTGAGE.rates[years];
  const months = years * 12;
  const payment = loan > 0 ? round2(loanMonthlyPayment(loan, rate, months)) : 0;
  const insuranceMonthly = round2((loan * MORTGAGE.insurance[insurance]) / 12);
  const monthly = round2(payment + insuranceMonthly);
  const salary = monthlyIncome(state);
  return {
    kind,
    price,
    notary,
    guarantee,
    fileFee: file,
    apport: used,
    loan,
    years,
    rate,
    payment,
    insurance,
    insuranceMonthly,
    monthly,
    totalInterest: round2(payment * months - loan),
    totalInsurance: round2(insuranceMonthly * months),
    debtRatio: salary > 0 ? (monthlyLoanPayments(state) + monthly) / salary : Infinity,
  };
}

/** Pourquoi la banque refuserait ce crédit, ou null si elle donne son accord. */
export function purchaseRefusal(
  state: GameState,
  plan: PurchasePlan,
): { key: TextKey; params: Record<string, string> } | null {
  if (state.ficp || state.surendettement) return { key: 'property.refuseFicp', params: {} };
  if (state.firstJobDay === null || state.stage === 'prologue') return { key: 'property.refuseJob', params: {} };
  const min = minimumApport(state, plan.kind);
  if (plan.loan > 0 && plan.apport < min) {
    return { key: 'property.refuseApport', params: { min: fmtEur(min), available: fmtEur(moneyAvailable(state)) } };
  }
  if (plan.apport > moneyAvailable(state)) return { key: 'property.refuseMoney', params: {} };
  if (plan.loan > 0 && plan.debtRatio > MORTGAGE.maxDebtRatio) {
    return {
      key: 'property.refuseRatio',
      params: { monthly: fmtEur(plan.monthly), ratio: fmtPct(plan.debtRatio), max: fmtPct(MORTGAGE.maxDebtRatio) },
    };
  }
  return null;
}

function setLine(state: GameState, id: ExpenseId, amount: number): void {
  state.expenses[id] = { amount, examined: 0, optimized: false };
}

/** Les dépenses d'un propriétaire : plus de loyer, mais la taxe foncière, la copropriété, l'énergie et l'assurance. */
function setOwnerExpenses(state: GameState, kind: PropertyKind): void {
  const def = PROPERTIES[kind];
  const at = (amount: number) => Math.round(amount * state.priceLevel);
  delete state.expenses.logement;
  setLine(state, 'taxeFonciere', at(def.propertyTax / 12));
  if (def.copropriete > 0) setLine(state, 'copropriete', at(def.copropriete));
  else delete state.expenses.copropriete;
  if (def.charges > 0) setLine(state, 'charges', at(def.charges));
  else delete state.expenses.charges;
  setLine(state, 'energie', at(def.energy));
  setLine(state, 'assurance', at(def.insurance));
}

/** La box internet d'un logement à soi, à son nom et au prix entier (la part de colocation ne tient plus). */
function ownInternet(state: GameState): void {
  const full = Math.round(HOUSING.studio.internet * state.priceLevel);
  const box = findSubscription(state, 'box_internet');
  if (!box) {
    subscribe(state, 'box_internet', full);
    state.flags = { ...state.flags, boxInternet: true };
    return;
  }
  if (box.shared) {
    changeSubscription(state, 'box_internet', full - box.amount);
    box.shared = false;
  }
}

/** Signer : l'apport part, le crédit commence, le loyer s'arrête et le dépôt de garantie revient. */
export function buyHome(
  state: GameState,
  kind: PropertyKind,
  apport: number,
  years: MortgageYears,
  insurance: MortgageInsurance,
): void {
  if (state.home) return;
  const plan = purchasePlan(state, kind, apport, years, insurance);
  if (purchaseRefusal(state, plan)) return;
  const renting = state.housing === 'coloc' || state.housing === 'studio';
  const deposit = renting ? Math.round(state.expenses.logement?.amount ?? 0) : 0;
  payFromSavings(state, plan.apport);
  if (deposit > 0) state.cash = round2(state.cash + deposit);
  state.housing = 'proprio';
  setOwnerExpenses(state, kind);
  ownInternet(state);
  if (plan.loan > 0) {
    const loan = addLoan(state, 'loan.mortgage', plan.loan, plan.rate, plan.years * 12);
    loan.mortgage = true;
    loan.insuranceMonthly = plan.insuranceMonthly;
  }
  state.home = {
    kind,
    price: plan.price,
    indexAtPurchase: state.market.realEstate,
    boughtDay: state.day,
    insurance,
    borrowed: plan.loan,
    paidOffDay: null,
  };
  state.flags = { ...state.flags, fraisHabitation: true };
  addWellbeing(state, 3);
  const property = t(PROPERTIES[kind].title);
  if (plan.loan > 0) {
    addJournal(state, 'journal.homeBought', {
      property,
      price: fmtEur(plan.price),
      loan: fmtEur(plan.loan),
      years: plan.years,
      monthly: fmtEur(plan.monthly),
    });
    unlockPage(state, 'taux_endettement');
    unlockPage(state, 'assurance_emprunteur');
  } else {
    addJournal(state, 'journal.homeBoughtCash', { property, price: fmtEur(plan.price) });
    mortgageFinished(state);
  }
}

/** Le crédit de la résidence principale est remboursé : avant 55 ans, le projet « Un toit à moi » est rempli. */
export function mortgageFinished(state: GameState): void {
  const home = state.home;
  if (!home || home.paidOffDay !== null) return;
  home.paidOffDay = state.day;
  const age = ageOnDay(state.birthDate, state.startDate, state.day);
  if (state.project === 'toit' && age < MORTGAGE.projectAge) {
    state.flags = { ...state.flags, projetToit: true };
    addNotice(state, 'home.projectDone', { age });
    addJournal(state, 'home.projectDone', { age });
  } else {
    addJournal(state, 'home.paidOff', { age });
  }
}

/** L'âge du dernier remboursement, au rythme actuel. */
export function mortgageEndAge(state: GameState): number | null {
  const loan = mortgageLoan(state);
  if (!loan) return null;
  return ageOnDay(state.birthDate, state.startDate, state.day + (loan.monthsLeft * 365) / 12);
}

/** Le plus petit remboursement anticipé possible : 10 % de la somme empruntée, ou ce qui reste dû. */
export function prepayMinimum(state: GameState): number {
  const loan = mortgageLoan(state);
  if (!loan || !state.home) return 0;
  return Math.min(round2(loan.balance), Math.ceil(state.home.borrowed * MORTGAGE.prepayMinShare));
}

/** Les pénalités d'un remboursement anticipé : le moins cher entre six mois d'intérêts et 3 % du capital restant dû. */
function prepayPenalty(loan: Loan, amount: number): number {
  return round2(
    Math.min((amount * loan.annualRate * MORTGAGE.penaltyMonths) / 12, MORTGAGE.penaltyCap * loan.balance),
  );
}

/** Le nombre de mensualités qu'il faut encore pour rembourser ce capital, à mensualité égale. */
function monthsToRepay(balance: number, payment: number, monthlyRate: number): number {
  if (monthlyRate === 0) return Math.ceil(balance / payment);
  return Math.ceil(-Math.log(1 - (monthlyRate * balance) / payment) / Math.log(1 + monthlyRate));
}

export interface PrepayPreview {
  amount: number;
  penalty: number;
  full: boolean;
  monthsBefore: number;
  monthsAfter: number;
  paymentAfter: number;
  interestSaved: number;
  insuranceSaved: number;
  /** Ce que la même somme rapporterait au Livret A pendant les mois de crédit qui restent. */
  livretGain: number;
}

/** Ce que changerait un remboursement anticipé : les mois gagnés, les intérêts économisés, et la comparaison au livret. */
export function prepayPreview(state: GameState, amount: number): PrepayPreview | null {
  const loan = mortgageLoan(state);
  if (!loan) return null;
  const value = round2(Math.min(amount, loan.balance));
  const rest = round2(loan.balance - value);
  const monthsAfter = rest > 0 ? monthsToRepay(rest, loan.monthlyPayment, loan.annualRate / 12) : 0;
  const paymentAfter = rest > 0 ? round2(loanMonthlyPayment(rest, loan.annualRate, monthsAfter)) : 0;
  const interestBefore = loan.monthlyPayment * loan.monthsLeft - loan.balance;
  const interestAfter = paymentAfter * monthsAfter - rest;
  return {
    amount: value,
    penalty: prepayPenalty(loan, value),
    full: rest <= 0,
    monthsBefore: loan.monthsLeft,
    monthsAfter,
    paymentAfter,
    interestSaved: round2(interestBefore - interestAfter),
    insuranceSaved: round2((loan.insuranceMonthly ?? 0) * (loan.monthsLeft - monthsAfter)),
    livretGain: round2(value * ((1 + LIVRET_A.rate) ** (loan.monthsLeft / 12) - 1)),
  };
}

/** Rembourser par anticipation : la mensualité ne change presque pas, le crédit finit plus tôt. */
export function prepayMortgage(state: GameState, amount: number): void {
  const loan = mortgageLoan(state);
  const preview = prepayPreview(state, amount);
  if (!loan || !preview) return;
  if (!preview.full && preview.amount < prepayMinimum(state)) return;
  const total = round2(preview.amount + preview.penalty);
  if (moneyAvailable(state) < total) return;
  payFromSavings(state, total);
  const params = {
    amount: fmtEur(preview.amount),
    penalty: fmtEur(preview.penalty),
    months: preview.monthsBefore - preview.monthsAfter,
  };
  if (preview.full) {
    state.loans = state.loans.filter((item) => item !== loan);
    addJournal(state, 'home.prepaidAll', params);
    mortgageFinished(state);
    return;
  }
  loan.balance = round2(loan.balance - preview.amount);
  loan.totalInterest = round2(loan.totalInterest - preview.interestSaved);
  loan.monthsLeft = preview.monthsAfter;
  loan.monthlyPayment = preview.paymentAfter;
  addJournal(state, 'home.prepaid', params);
}

export interface SalePreview {
  value: number;
  agency: number;
  balance: number;
  penalty: number;
  /** Ce qui reste de la vente, crédit soldé. */
  net: number;
  /** S'installer en location dans un studio : dépôt de garantie, frais d'agence, déménagement. */
  moving: number;
}

export function salePreview(state: GameState): SalePreview | null {
  if (!state.home) return null;
  const value = homeValue(state);
  const agency = round2(value * MORTGAGE.agencyRate);
  const loan = mortgageLoan(state);
  const balance = loan ? round2(loan.balance) : 0;
  const penalty = loan ? prepayPenalty(loan, balance) : 0;
  return {
    value,
    agency,
    balance,
    penalty,
    net: round2(value - agency - balance - penalty),
    moving: moveOutCost(state, 'studio'),
  };
}

/** On peut vendre si la vente, plus l'épargne, couvre le crédit et le déménagement. */
export function canSellHome(state: GameState): boolean {
  const preview = salePreview(state);
  return preview !== null && preview.net - preview.moving + moneyAvailable(state) >= 0;
}

/** Revendre : le crédit est soldé, ce qui reste revient sur le compte, et on loue un studio. */
export function sellHome(state: GameState): void {
  const preview = salePreview(state);
  if (!preview || !canSellHome(state)) return;
  state.loans = state.loans.filter((loan) => !loan.mortgage);
  state.cash = round2(state.cash + preview.net);
  delete state.expenses.taxeFonciere;
  delete state.expenses.copropriete;
  state.home = null;
  payFromSavings(state, preview.moving);
  moveOut(state, 'studio');
  addJournal(state, 'home.sold', { value: fmtEur(preview.value), net: fmtEur(preview.net) });
}

/** Changer d'assurance emprunteur pour une assurance déléguée, moins chère : la loi le permet à tout moment. */
export function delegateMortgageInsurance(state: GameState): void {
  const loan = mortgageLoan(state);
  const home = state.home;
  if (!loan || !home || home.insurance === 'deleguee') return;
  loan.insuranceMonthly = round2((home.borrowed * MORTGAGE.insurance.deleguee) / 12);
  home.insurance = 'deleguee';
}

/** L'économie par mois d'une assurance déléguée, par rapport à celle de la banque. */
export function delegationSaving(state: GameState): number {
  const loan = mortgageLoan(state);
  const home = state.home;
  if (!loan || !home || home.insurance === 'deleguee') return 0;
  return round2((loan.insuranceMonthly ?? 0) - (home.borrowed * MORTGAGE.insurance.deleguee) / 12);
}

/** Renégocier le taux : la mensualité baisse, la durée reste la même. */
export function cutMortgageRate(state: GameState, cut: number): void {
  const loan = mortgageLoan(state);
  if (!loan) return;
  loan.annualRate = Math.max(0, loan.annualRate - cut);
  const payment = round2(loanMonthlyPayment(loan.balance, loan.annualRate, loan.monthsLeft));
  loan.totalInterest = round2(loan.totalInterest - (loan.monthlyPayment - payment) * loan.monthsLeft);
  loan.monthlyPayment = payment;
}

/** La baisse de mensualité qu'apporterait une baisse de taux. */
export function rateCutSaving(state: GameState, cut: number): number {
  const loan = mortgageLoan(state);
  if (!loan) return 0;
  const payment = loanMonthlyPayment(loan.balance, Math.max(0, loan.annualRate - cut), loan.monthsLeft);
  return round2(loan.monthlyPayment - payment);
}
